import {
  Divider, NoData, Spinner
} from "@whitespace/whitespace-ui";
import {
  Card, Col, ListGroup, Row
} from "react-bootstrap";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { IRequest } from "interfaces/models/IRequest";
import { useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { updateRequestsFilter } from "actions/updateRequestsFilter";
import IRequestsResponseItem from "interfaces/responses/IRequestsResponseItem";
import { mapRequestsResponseItemToRequest } from "mappers/mapRequestsResponseItemToRequest/mapRequestsResponseItemToRequest";
import AmplifyError from "components/AmplifyError/AmplifyError";
import SearchEntry from "components/SearchEntry/SearchEntry";
import RequestItem from "./RequestItem/RequestItem";

const Requests = () => {
  const { state } = useStateMachine();
  const {
    isSending, errors, data, sendRequest
  } = useAmplifyREST();

  const { actions } = useStateMachine({
    updateRequestsFilter
  });

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [requests, setRequests] = useState<IRequest[]|undefined>(undefined);
  const [filteredRequests, setFilteredRequests] = useState<IRequest[] | undefined>(undefined);

  const [isSendingUserToApplicationApi, setIsSendingUserToApplicationApi] = useState(false);
  const [userToApplicationApiErrors, setUserToApplicationApiErrors] = useState<string[]>([]);

  const anySending = isSending || isSendingUserToApplicationApi;
  const anyErrors = errors || userToApplicationApiErrors;

  const handleDeny = () => {
    sendRequest("requests", "GET");
  };

  const renderRequests = () => filteredRequests!.map((request: IRequest, idx:number) => (
    <RequestItem
      key={`requestItem_${idx}`}
      requestIndex={idx}
      request={request}
      handleIsSending={setIsSendingUserToApplicationApi}
      handleHasErrored={setUserToApplicationApiErrors}
      handleDeny={handleDeny}
    />
  ));

  const handleTextSearch = (searchText: string) => {
    if (state.requestsFilter) {
      const requestsFilter = { ...state.requestsFilter };
      requestsFilter.searchText = searchText;
      actions.updateRequestsFilter(requestsFilter);
    }
  };

  const filterRequests = () => {
    if (requests) {
      let requestsFiltered = requests;

      if (state.requestsFilter!.searchText) {
        const searchTerm = new RegExp(state.requestsFilter!.searchText, "gi");
        requestsFiltered = requestsFiltered.filter(request => request.username.match(searchTerm)
           || request.emailAddress.match(searchTerm) || request.site.match(searchTerm));
      }

      setFilteredRequests(requestsFiltered);
    }
  };

  useEffect(() => {
    sendRequest("requests", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (state.requestsFilter) {
      filterRequests();
    }
  }, [state.requestsFilter]);

  useEffect(() => {
    if (data) {
      const requestResponse = data as IRequestsResponseItem[];
      const requestList = requestResponse.map(
        (requestResponseItem : IRequestsResponseItem) => mapRequestsResponseItemToRequest(requestResponseItem)
      );

      setRequests(requestList);
      setFilteredRequests(requestList);
    }
  }, [data]);

  useEffect(() => {
    if (!isFilterApplied && requests) {
      filterRequests();
      setIsFilterApplied(true);
    }
  }, [isFilterApplied, requests]);

  return (
    <>
      {anyErrors && <AmplifyError errors={anyErrors} />}
      <Card>
        <Card.Header>
          <h1 data-testid="requestsHeader">
            Access Requests
            <Spinner animation="grow" visible={anySending} spinnerText="Loading" variant="info" />
          </h1>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="w-100 align-items-center top-margin-small bottom-margin-small justify-content-end">
            <Col xs={8} className="d-flex user-filter justify-content-end">
              <SearchEntry defaultValue={state.requestsFilter!.searchText!} onSearch={handleTextSearch} />
            </Col>
          </Row>
          <Divider />
          <Row className="list-heading">
            <Col xs={5} md={4} lg={5} data-testid="headingUsername">
              <Row>
                <Col xs={1} />
                <Col xs={11}>
                  User/Email
                </Col>
              </Row>
            </Col>
            <Col xs={7} md={6} lg={4} data-testid="headingApplicationGroup">Site</Col>
            <Col xs={2} className="d-none d-lg-block" data-testid="headingTimestamp">Date/Time</Col>
            <Col xs={12} lg={1} className="d-flex justify-content-center" data-testid="headingApproval">Approval</Col>
          </Row>
          <Divider className="no-display-sm" />
          {filteredRequests
                  && (
                    <>
                      <NoData visible={filteredRequests.length === 0} description="No Requests Found" />
                      {filteredRequests.length > 0
                      && (
                        <ListGroup variant="flush" className="striped highlight">
                          { renderRequests()}
                        </ListGroup>
                      )}
                    </>
                  )}
        </Card.Body>
      </Card>
    </>
  );
};
export default Requests;
